import { isValidElement } from 'react';

import { styled } from '@mui/material';

import {
  LabelContainerButtonSizeMap,
  LabelContainerLinkSizeMap,
  LabelContainerStatusSizeMap,
} from './constant';
import { labelContainerClasses } from './theme';

import type { ReactElement, ReactNode } from 'react';
import type { LabelSize } from '../Label/types';
import type { LabelContainerOwnerState, LabelContainerProps } from './types';

const LabelContainer = (props: LabelContainerProps) => {
  const { label, actions, children, ...others } = props;

  const ownerState: LabelContainerOwnerState = props;

  // Label의 size 추출 (기본값: sm)
  const labelSize: LabelSize = label.props.size || 'sm';

  // size에 맞는 Status, Link, Button size 매핑
  const statusSize = LabelContainerStatusSizeMap[labelSize];
  const linkSize = LabelContainerLinkSizeMap[labelSize];
  const buttonSize = LabelContainerButtonSizeMap[labelSize];

  const renderElementWithDefaultSize = <T extends Record<string, unknown>>(
    element: ReactNode,
    size: string,
  ): ReactElement<T> | null => {
    if (!isValidElement(element)) return null;

    const Component = element.type;
    const key = element.key;
    const props = element.props as T;

    return <Component key={key} {...props} size={props.size ?? size} />;
  };

  const renderActions = () => {
    if (!actions) return;

    const { status, links, buttons } = actions;
    const hasLinks = !!links && links.length > 0;
    const hasButtons = !!buttons && buttons.length > 0;

    if (!status && !hasLinks && !hasButtons) return null;

    return (
      <LabelContainerActionsContainer ownerState={ownerState}>
        {status && (
          <LabelContainerStatusContainer ownerState={ownerState}>
            {renderElementWithDefaultSize(status, statusSize)}
          </LabelContainerStatusContainer>
        )}

        {hasLinks && (
          <LabelContainerLinksContainer ownerState={ownerState}>
            {links.map(link => renderElementWithDefaultSize(link, linkSize))}
          </LabelContainerLinksContainer>
        )}

        {hasButtons && (
          <LabelContainerButtonsContainer ownerState={ownerState}>
            {buttons.map(button => renderElementWithDefaultSize(button, buttonSize))}
          </LabelContainerButtonsContainer>
        )}
      </LabelContainerActionsContainer>
    );
  };

  return (
    <LabelContainerRoot ownerState={ownerState} {...others}>
      <LabelContainerContent ownerState={ownerState}>
        <LabelContainerLabelArea ownerState={ownerState}>{label}</LabelContainerLabelArea>
        {renderActions()}
      </LabelContainerContent>
      {children}
    </LabelContainerRoot>
  );
};

const LabelContainerRoot = styled('div', {
  target: labelContainerClasses.root,
})<{ ownerState: LabelContainerOwnerState }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const LabelContainerContent = styled('div', {
  target: labelContainerClasses.content,
})<{ ownerState: LabelContainerOwnerState }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 16px;
  row-gap: 4px;
`;

const LabelContainerLabelArea = styled('div', {
  target: labelContainerClasses.labelArea,
})<{ ownerState: LabelContainerOwnerState }>`
  display: flex;
  flex: 1;
  min-width: 164px;
`;

const LabelContainerActionsContainer = styled('div', {
  target: labelContainerClasses.actions,
})<{ ownerState: LabelContainerOwnerState }>`
  display: flex;
  flex-shrink: 0;
  gap: 20px;
  align-self: ${({ ownerState }) => (ownerState.label?.props?.description ? 'flex-end' : 'center')};
`;

const LabelContainerStatusContainer = styled('div', {
  target: labelContainerClasses.status,
})<{ ownerState: LabelContainerOwnerState }>`
  display: flex;
  align-items: center;
`;

const LabelContainerLinksContainer = styled('div', {
  target: labelContainerClasses.links,
})<{ ownerState: LabelContainerOwnerState }>`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const LabelContainerButtonsContainer = styled('div', {
  target: labelContainerClasses.buttons,
})<{ ownerState: LabelContainerOwnerState }>`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export default LabelContainer;
